import { useQuery } from '@tanstack/react-query';
import { Eye, Trash2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type MerchantPaymentMethodTokenListItem } from '@pillar-portal/api-types';
import { ApiError, paymentsApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { describePaymentMethod } from '@/lib/payment-methods/describePaymentMethod';

function CardVaultListItem({
    paymentMethodToken,
    onOpenDetails,
}: {
    paymentMethodToken: MerchantPaymentMethodTokenListItem;
    onOpenDetails: (paymentMethodTokenUuid: string) => void;
}) {
    const description = describePaymentMethod(paymentMethodToken);
    const deleteClickHandler = paymentMethodToken.can_display
        ? () => {
              onOpenDetails(paymentMethodToken.payment_method_token_uuid);
          }
        : undefined;

    return (
        <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
            <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground">
                        {paymentMethodToken.holder_organization_name}
                    </p>
                    <h3 className="font-semibold">{description.title}</h3>
                    <p className="text-sm text-muted-foreground">{description.subtitle}</p>
                </div>
                <div className="flex gap-2 ml-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            onOpenDetails(paymentMethodToken.payment_method_token_uuid);
                        }}
                        title="View payment method"
                        aria-label={`View ${description.title}`}
                        disabled={!paymentMethodToken.can_display}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={deleteClickHandler}
                        title="Delete payment method"
                        aria-label={`Delete ${description.title}`}
                        disabled={!paymentMethodToken.can_delete}
                        className="text-red-600 hover:text-red-700"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

export function CardVaultContent() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const handleOpenDetails = (paymentMethodTokenUuid: string): void => {
        breadcrumbNavigate({
            source: 'panel',
            target: {
                url: `/card-vault/${paymentMethodTokenUuid}`,
                title: 'Payment Method Details',
            },
        });
    };
    const paymentMethodTokensQuery = useQuery({
        queryKey: ['merchantPaymentMethodTokens', 'cardVault'],
        queryFn: async () =>
            await paymentsApi.listMerchantPaymentMethodTokens({
                limit: 1000,
            }),
        retry: false,
    });

    if (paymentMethodTokensQuery.isLoading) {
        return <div className="p-6 text-sm text-muted-foreground">Loading Card Vault...</div>;
    }

    if (paymentMethodTokensQuery.error !== null) {
        const errorMessage: string =
            paymentMethodTokensQuery.error instanceof ApiError
                ? paymentMethodTokensQuery.error.message
                : 'Failed to load Card Vault.';

        return (
            <div className="p-6">
                <Alert variant="destructive">
                    <AlertTitle>Unable to load Card Vault</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
            </div>
        );
    }

    const paymentMethodTokens: ReadonlyArray<MerchantPaymentMethodTokenListItem> =
        paymentMethodTokensQuery.data?.payment_method_tokens ?? [];

    return (
        <div className="space-y-6 p-6">
            <div className="space-y-1">
                <h1 className="text-3xl font-semibold tracking-tight">Card Vault</h1>
                <p className="text-sm text-muted-foreground">
                    Review stored payment methods that are visible to your active role assignment.
                </p>
            </div>

            {paymentMethodTokens.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No payment methods</CardTitle>
                        <CardDescription>
                            No stored payment methods are currently visible in Card Vault.
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>Payment Methods</CardTitle>
                        <CardDescription>
                            View stored payment methods and the operations available to you.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {paymentMethodTokens.map((paymentMethodToken) => (
                            <CardVaultListItem
                                key={paymentMethodToken.payment_method_token_uuid}
                                paymentMethodToken={paymentMethodToken}
                                onOpenDetails={handleOpenDetails}
                            />
                        ))}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
